import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthFrame, { authInput, planFrom } from "@/components/auth/AuthFrame";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import GoogleButton from "@/components/GoogleButton";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const next = sp.get("next") || "/account";
    const light = !!sp.get("next");
    const plan = planFrom(next);
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const cls = authInput(light);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try { await register(form); navigate(next); }
        catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <AuthFrame light={light} plan={plan} next={next}
            title={plan ? "Create your account" : "Create your account"}
            sub={plan ? `Then you'll pay for the ${plan.name} and set your shop up.`
                : "Faster checkout, order history, one-tap reorders."}
            footer={
                <p className={`mt-6 text-sm ${light ? "text-zinc-600" : "text-center text-zinc-400"}`}>
                    Already have an account?{" "}
                    <Link to={next && next !== "/account" ? `/login?next=${encodeURIComponent(next)}` : "/login"}
                        className={`font-semibold ${light ? "text-zinc-950 underline decoration-2" : "text-brand-green"}`}>
                        Log in
                    </Link>
                </p>
            }>
            <div className="mt-8 space-y-4">
                <GoogleButton label="Sign up with Google" light={light} />
                <div className="flex items-center gap-3">
                    <span className={`h-px flex-1 ${light ? "bg-zinc-200" : "bg-white/10"}`} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">or email</span>
                    <span className={`h-px flex-1 ${light ? "bg-zinc-200" : "bg-white/10"}`} />
                </div>
            </div>
            <form onSubmit={submit} className="mt-4 space-y-4">
                <input className={cls} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="register-name" />
                <input className={cls} type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="register-email" />
                <input className={cls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="register-phone" />
                <input className={cls} type="password" placeholder="Password" value={form.password} onChange={set("password")} data-testid="register-password" />
                {err && <p className={`text-sm ${light ? "text-red-600" : "text-red-400"}`} data-testid="register-error">{err}</p>}
                <GreenButton className="w-full" type="submit" disabled={busy} data-testid="register-submit">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : plan ? "Create account & continue" : "Sign Up"}
                </GreenButton>
            </form>
        </AuthFrame>
    );
}
